import { forwardRef } from 'react';
import type { SnippetSummaryResponse } from '../api';
import { Button, EmptyState, Spinner } from '../components/ui';
import { languageLabel } from '../editor/languages';

interface SnippetListProps {
  items: SnippetSummaryResponse[];
  total: number;
  fuzzy: boolean;
  query: string;
  selectedId: string | null;
  isPending: boolean;
  canCreate: boolean;
  libraryName: string;
  onQueryChange: (query: string) => void;
  onSelect: (id: string) => void;
  onCreate: () => void;
}

export const SnippetList = forwardRef<HTMLInputElement, SnippetListProps>(function SnippetList(
  {
    items,
    total,
    fuzzy,
    query,
    selectedId,
    isPending,
    canCreate,
    libraryName,
    onQueryChange,
    onSelect,
    onCreate,
  },
  searchRef,
) {
  return (
    <div className="flex h-full w-full flex-col border-r border-border-subtle md:w-80">
      <div className="flex items-center gap-2 border-b border-border-subtle px-3 py-2">
        <input
          ref={searchRef}
          type="search"
          value={query}
          aria-label="Search snippets"
          placeholder="Search…  (press /)"
          onChange={(event) => onQueryChange(event.target.value)}
          className="min-w-0 flex-1 rounded-md border border-border-subtle bg-surface px-2 py-1.5
            text-sm text-text placeholder:text-text-faint"
        />

        <Button
          variant="primary"
          onClick={onCreate}
          disabled={!canCreate}
          title={canCreate ? `New snippet in ${libraryName}` : 'Not available in this library'}
          aria-label="New snippet"
        >
          +
        </Button>
      </div>

      {/* Which library is showing, so the list is never ambiguous about whose snippets these are. */}
      <p className="border-b border-border-subtle px-3 py-1.5 text-xs text-text-faint">
        {libraryName}
      </p>

      {fuzzy && (
        <p className="border-b border-border-subtle px-3 py-2 text-xs text-text-muted">
          No exact matches. Showing snippets with similar titles.
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isPending ? (
          <div className="p-4">
            <Spinner label="Searching" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState title={query ? 'Nothing matches that' : 'Your drawer is empty'}>
            {query
              ? 'Try fewer words, or a different tag.'
              : 'Create a snippet to get started.'}
          </EmptyState>
        ) : (
          <ul>
            {items.map((snippet) => (
              <li key={snippet.id}>
                <button
                  type="button"
                  aria-current={snippet.id === selectedId ? 'true' : undefined}
                  // Named explicitly rather than by its contents: the tile also carries languages
                  // and tag names, which makes for a long, unstable accessible name.
                  aria-label={`Open ${snippet.title}`}
                  onClick={() => onSelect(snippet.id)}
                  className={`flex w-full flex-col gap-1 border-b border-border-subtle px-3 py-2.5
                    text-left transition-colors
                    ${
                      snippet.id === selectedId
                        ? 'bg-accent-subtle'
                        : 'hover:bg-surface-raised'
                    }`}
                >
                  <span className="flex items-center gap-1.5">
                    {snippet.isFavorite && (
                      <span aria-label="Favourite" className="text-xs text-accent">
                        ★
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-text">
                      {snippet.title}
                    </span>
                  </span>

                  {snippet.description && (
                    <span className="truncate text-xs text-text-muted">{snippet.description}</span>
                  )}

                  <span className="flex flex-wrap items-center gap-1.5 text-xs text-text-faint">
                    {snippet.languages.slice(0, 3).map((language) => (
                      <span key={language}>{languageLabel(language)}</span>
                    ))}
                    {snippet.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center gap-1 rounded-full bg-surface-raised
                          px-1.5 py-0.5 text-text-muted"
                      >
                        {tag.color && (
                          <span
                            aria-hidden="true"
                            className="size-1.5 rounded-full"
                            style={{ backgroundColor: tag.color }}
                          />
                        )}
                        {tag.name}
                      </span>
                    ))}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="border-t border-border-subtle px-3 py-1.5 text-xs text-text-faint">
        {total} {total === 1 ? 'snippet' : 'snippets'}
      </p>
    </div>
  );
});
